// src/pages/AssistantsList.js
"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar, Toolbar, Container, Typography, Box, Grid, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip, Stack,
  CircularProgress, Fab, Skeleton, InputAdornment, Divider, Avatar, Menu, MenuItem,
  ListItemIcon, FormControl, Select, Paper, Backdrop
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import WorkspacesIcon from "@mui/icons-material/Workspaces";

import axios from "axios";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../auth/AuthContext";
import ThemeToggleButton from "../components/ThemeToggleButton";
import PlanBadge from "../components/PlanBadge";
import { getMe } from "../api/billing";
import ChatInterface from "../components/ChatInterface"; // <-- NEW: embed chat in modal
import Slide from "@mui/material/Slide";



const ChatTransition = React.forwardRef(function ChatTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const API = process.env.REACT_APP_API;

/* -------------------- NEW: Training status helpers -------------------- */
const ACTIVE_JOB_STATES = new Set(["queued", "processing"]);

function statusChipForAssistant(a) {
  // Prefer explicit async job status if present
  const status = (a.TrainingStatus || "").toLowerCase();
  const hasPending = !!a.HasPendingTraining;
  const trained = !!a.Trained;

  if (status === "queued")     return { color: "warning", variant: "filled",   label: "Queued" };
  if (status === "processing") return { color: "primary", variant: "filled",   label: "Training…" };
  if (status === "completed")  return { color: "success", variant: "filled",   label: "Trained" };
  if (status === "failed")     return { color: "error",   variant: "filled",   label: "Failed" };
  if (status === "timeout")    return { color: "error",   variant: "outlined", label: "Timed out" };

  // Fallback to existing summary flags (for older rows/backends)
  if (hasPending) return { color: "warning", variant: "outlined", label: "Changes pending" };
  if (trained)    return { color: "success", variant: "filled",   label: "Trained" };
  return { color: "warning", variant: "outlined", label: "Not trained" };
}
/* --------------------------------------------------------------------- */

export default function AssistantsList() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [assistants, setAssistants] = useState([]);
  const [openDialog, setOpenDialog] = useState(false); // create or edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", purpose: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [me, setMe] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // default 5 per page

  // user id from Cognito
  const userId = user?.sub;

  // --- NEW: Chat modal state ---
  const [chatOpen, setChatOpen] = useState(false);
  const [chatAssistant, setChatAssistant] = useState(null); // entire assistant row (for title)
  const [chatAssistantId, setChatAssistantId] = useState(null);

  const logoSrc =
    theme.palette.mode === "dark"
      ? "/brand/SemiQolon.png"
      : "/brand/SemiQolon-black.png";

  const fetchAssistants = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/assistants`, { params: { user_id: userId } });
      setAssistants(res.data?.items || []);
    } catch {
      toast.error("Failed to load assistants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (!userId) return;
      try { setMe(await getMe(userId)); } catch {}
      await fetchAssistants();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const openWorkspace = (assistantId) => {
    localStorage.setItem("assistantId", assistantId);
    navigate(`/workspace?assistantId=${assistantId}`);
  };

  // NEW: open chat modal in-place on listing
  const openChatModal = (assistant) => {
    setChatAssistant(assistant || null);
    setChatAssistantId(assistant?.AssistantID || null);
    try { localStorage.setItem("assistantId", assistant?.AssistantID || ""); } catch {}
    setChatOpen(true);
  };
  const closeChatModal = () => {
    setChatOpen(false);
    setChatAssistant(null);
    setChatAssistantId(null);
  };

  const onCreate = () => {
    setEditingId(null);
    setForm({ name: "", purpose: "", description: "" });
    setOpenDialog(true);
  };

  const onEdit = (a) => {
    setEditingId(a.AssistantID);
    setForm({ name: a.Name, purpose: a.Purpose || "", description: a.Description || "" });
    setOpenDialog(true);
  };

  const saveAssistant = async () => {
    if (!form.name.trim()) return toast.error("Assistant name is required");
    try {
      setSaving(true);
      if (editingId) {
        const res = await axios.put(`${API}/assistants/${editingId}`, {
          user_id: userId,
          name: form.name.trim(),
          purpose: form.purpose.trim(),
          description: form.description.trim(),
        });
        setAssistants((prev) => prev.map((x) => (x.AssistantID === editingId ? res.data : x)));
        toast.success("Assistant updated");
      } else {
        await axios.post(`${API}/assistants`, {
          user_id: userId,
          name: form.name.trim(),
          purpose: form.purpose.trim(),
          description: form.description.trim(),
        });
        // Important: re-fetch so we get server-side training status/job IDs
        await fetchAssistants();
        toast.success("Assistant created");
      }
      setOpenDialog(false);
      setPage(1); // go to first page after add/edit
    } catch (e) {
      toast.error(e?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (assistantId) => {
    try {
      await axios.delete(`${API}/assistants/${assistantId}`, { params: { user_id: userId } });
      setAssistants((prev) => prev.filter((a) => a.AssistantID !== assistantId));
      toast.success("Assistant deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // filter + sort
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...assistants].sort((a, b) =>
      String(b.UpdatedAt || "").localeCompare(String(a.UpdatedAt || ""))
    );
    if (!q) return sorted;
    return sorted.filter((a) =>
      [a.Name, a.Purpose, a.Description].filter(Boolean).some((t) => t.toLowerCase().includes(q))
    );
  }, [assistants, query]);

  // clamp page when results or pageSize change
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [filtered.length, pageSize, page]);

  const start = (page - 1) * pageSize;
  const end = Math.min(start + pageSize, filtered.length);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice(start, end);

  // reset to first page when search changes
  useEffect(() => { setPage(1); }, [query]);

  // --- profile menu ---
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const initials = (user?.email || "?").slice(0, 1).toUpperCase();

  const heroBg =
    theme.palette.mode === "light"
      ? "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)"
      : "linear-gradient(180deg, #0b1020 0%, #0f172a 100%)";

  /* -------------------- NEW: Live job status wiring -------------------- */
  const [jobByAssistant, setJobByAssistant] = useState({}); // { [AssistantID]: { job_id, status } }

  // Seed local job map from backend list (if it includes TrainingJobId/TrainingStatus)
  useEffect(() => {
    const seeded = {};
    assistants.forEach(a => {
      if (a.TrainingJobId) {
        seeded[a.AssistantID] = {
          job_id: a.TrainingJobId,
          status: (a.TrainingStatus || "").toLowerCase(),
        };
      }
    });
    if (Object.keys(seeded).length) {
      setJobByAssistant(prev => ({ ...prev, ...seeded }));
    }
  }, [assistants]);

  // Poll active jobs: queued/processing
  useEffect(() => {
    const actives = assistants
      .filter(a => {
        const st = (a.TrainingStatus || jobByAssistant[a.AssistantID]?.status || "").toLowerCase();
        return a.TrainingJobId && ACTIVE_JOB_STATES.has(st);
      })
      .map(a => ({ id: a.AssistantID, jobId: a.TrainingJobId }));

    if (actives.length === 0) return;

    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const updates = await Promise.all(
          actives.map(async ({ id, jobId }) => {
            const res = await axios.get(`${API}/assistants/${id}/train-status`, {
              params: { job_id: jobId },
              validateStatus: () => true
            });
            if (res.status !== 200) return null;
            const next = (res.data?.status || "").toLowerCase();
            return { id, jobId, status: next };
          })
        );
        if (cancelled) return;

        const byId = {};
        updates.filter(Boolean).forEach(u => { byId[u.id] = u; });

        if (Object.keys(byId).length) {
          setAssistants(prev => prev.map(a => {
            const upd = byId[a.AssistantID];
            if (!upd) return a;
            return {
              ...a,
              TrainingStatus: upd.status,
              TrainingJobId: upd.jobId,
              ...(upd.status === "completed" ? { Trained: true, HasPendingTraining: false } : {})
            };
          }));
          setJobByAssistant(prev => {
            const next = { ...prev };
            for (const k of Object.keys(byId)) {
              next[k] = { job_id: byId[k].jobId, status: byId[k].status };
            }
            return next;
          });
        }
      } catch { /* ignore transient poll errors */ }
    }, 2500);

    return () => { cancelled = true; clearInterval(timer); };
  }, [assistants, jobByAssistant]);
  /* -------------------------------------------------------------------- */

  return (
    <>
      <AppBar position="sticky" color="inherit">
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, textDecoration: "none" }}>
            <Box
              component="img"
              src={logoSrc}
              alt="SemiQolon Studio"
              sx={{ height: 32, width: "auto", display: "block" }}
              loading="eager"
            />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
          </Typography>

          <TextField
            size="small"
            placeholder="Search assistants…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Theme toggle */}
          <ThemeToggleButton />
          {me?.plan && <PlanBadge plan={me.plan} />}
          <Button component={RouterLink} to="/pricing" variant="outlined" size="small">
            Pricing
          </Button>

          {/* Profile menu */}
          <Tooltip title={user?.email || "Account"}>
            <IconButton onClick={openMenu}>
              <Avatar sx={{ width: 32, height: 32 }}>{initials}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
            <MenuItem component={RouterLink} to="/profile" onClick={closeMenu}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { closeMenu(); signOut(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box
        sx={{
          py: 5,
          background: heroBg,
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 2
        }}
      >
        <Container>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "start", md: "center" }}
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                Your ; Assistants
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create specialized agents, upload knowledge, train, and chat — all in one place.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip icon={<SmartToyIcon />} label={`${assistants.length} total`} />
                <Chip
                  icon={<AutoAwesomeIcon />}
                  label={`${assistants.filter(a => a.Trained).length} trained`}
                  color="success"
                  variant="outlined"
                />
                <Chip label={user?.email || ""} variant="outlined" />
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container sx={{ pb: 6 }}>
        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: pageSize }).map((_, i) => (
              <Grid item xs={12} key={i}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="30%" />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="rectangular" height={24} sx={{ mt: 1, borderRadius: 1 }} />
                    </Box>
                    <Skeleton variant="rounded" width={100} height={36} />
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <Box
            sx={{
              height: "40vh",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              flexDirection: "column",
              bgcolor: theme.palette.mode === "light" ? "#fafafa" : "transparent"
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              No assistants found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first assistant to get started.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={onCreate}>
              Create Assistant
            </Button>
          </Box>
        ) : (
          <>
            {/* ==== HORIZONTAL STRIPS LIST ==== */}
            <Stack spacing={1.25}>
              {pageItems.map((a) => {
                const uploadsCount = Array.isArray(a.Uploads) ? a.Uploads.length : 0;
                const updated = a.UpdatedAt ? new Date(a.UpdatedAt).toLocaleString() : "—";
                const chipCfg = statusChipForAssistant(a);
                const nameInitial = (a?.Name || "?").slice(0, 1).toUpperCase();

                return (
                  <Paper
                    key={a.AssistantID}
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      display: "grid",
                      gridTemplateColumns: "minmax(0,1fr) auto",
                      gap: 2,
                      alignItems: "center",
                      transition: "border-color .15s ease, background .15s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        background:
                          theme.palette.mode === "light"
                            ? "rgba(25,118,210,0.04)"
                            : "rgba(25,118,210,0.08)"
                      }
                    }}
                  >
                    {/* Left: avatar + details */}
                    <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
                      <Avatar sx={{ width: 48, height: 48, fontWeight: 700 }}>{nameInitial}</Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 800 }}
                            noWrap
                            title={a.Name}
                          >
                            {a.Name}
                          </Typography>
                          <Chip
                            size="small"
                            color={chipCfg.color}
                            variant={chipCfg.variant}
                            label={chipCfg.label}
                            sx={{ height: 22 }}
                          />
                        </Stack>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          title={a.Purpose || "—"}
                        >
                          {a.Purpose || "—"}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 1 }}
                        >
                          <Chip
                            size="small"
                            icon={<DescriptionIcon />}
                            label={`${uploadsCount} files`}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            icon={<ScheduleIcon />}
                            label={`Updated ${updated}`}
                            variant="outlined"
                          />
                        </Stack>

                        {a.Description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 1,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              maxWidth: { xs: "100%", md: 900 }
                            }}
                            title={a.Description}
                          >
                            {a.Description}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {/* Right: actions */}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{ minWidth: 300 }}
                    >
                      {/* NEW: Clickable "Chat" chip that opens modal */}
                      <Chip
                        label="Chat"
                        variant="outlined"
                        color="primary"
                        onClick={() => openChatModal(a)}
                        icon={<ChatBubbleOutlineIcon />}
                        clickable
                        sx={{
                          fontWeight: 700,
                          "& .MuiChip-icon": { fontSize: 18 },
                        }}
                        aria-label={`Open chat for ${a.Name}`}
                      />

                      <Tooltip title="Open workspace">
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<WorkspacesIcon />}
                            onClick={() => openWorkspace(a.AssistantID)}
                          >
                            Workspace
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => onEdit(a)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => onDelete(a.AssistantID)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            {/* Bottom pager */}
            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap"
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Showing <b>{start + 1}</b>–<b>{end}</b> of <b>{filtered.length}</b>
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small">
                  <Select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    displayEmpty
                  >
                    <MenuItem value={5}>5 / page</MenuItem>
                    <MenuItem value={8}>8 / page</MenuItem>
                    <MenuItem value={12}>12 / page</MenuItem>
                  </Select>
                </FormControl>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_e, v) => setPage(v)}
                  color="primary"
                />
              </Stack>
            </Box>
          </>
        )}
      </Container>

      {/* Create/Edit dialog */}
      <Dialog
        open={openDialog}
        onClose={() => { if (!saving) setOpenDialog(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingId ? "Edit Assistant" : "Create Assistant"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Assistant name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              disabled={saving}
            />
            <TextField
              label="Purpose"
              value={form.purpose}
              onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
              disabled={saving}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              multiline
              rows={3}
              disabled={saving}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveAssistant} disabled={saving}>
            {saving ? (editingId ? "Updating…" : "Creating…") : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW: Chat modal (renders ChatInterface inline) */}
    

      <Dialog
        open={chatOpen}
        onClose={closeChatModal}
        fullWidth
        maxWidth="xl"
        TransitionComponent={ChatTransition}
        keepMounted
        // keep it snappy and usable; no blur, no curves
        PaperProps={{
          sx: {
            width: { xs: "100%", md: "96%" },
            height: { xs: "100vh", md: "86vh" },
            maxHeight: "92vh",
            borderRadius: 0,          // ← flat corners
            m: 0,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header: simple, fixed height */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: (t) => (t.palette.mode === "dark" ? "#0b1020" : "#f8fafc"),
            flex: "0 0 auto",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
            <Avatar sx={{ width: 32, height: 32, fontWeight: 700 }}>
              {(chatAssistant?.Name || "A").slice(0, 1).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap title={chatAssistant?.Name || "Assistant"}>
                {chatAssistant?.Name || "Assistant"}
              </Typography>
              <Typography variant="caption" color="text.secondary">Quick chat</Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Open workspace">
              <Button
                size="small"
                variant="outlined"
                startIcon={<WorkspacesIcon />}
                onClick={() => { closeChatModal(); openWorkspace(chatAssistant?.AssistantID); }}
                sx={{ borderRadius: 1 }}
              >
                Workspace
              </Button>
            </Tooltip>
            <IconButton onClick={closeChatModal}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* CONTENT: the critical part — this must FLEX and SCROLL */}
        <DialogContent
          dividers
          sx={{
            p: 0,
            flex: "1 1 auto",
            minHeight: 0,         // ← allows children to compute height
            display: "flex",
            overflow: "hidden",   // keep only the inner container scrollable
          }}
        >
          {/* Inner scroll container: ChatInterface fills this. */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: "auto",    // ← actual scrolling happens here
              display: "flex",
            }}
          >
            {chatAssistantId ? (
              <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <ChatInterface
                  assistantId={chatAssistantId}
                  userId={userId || null}
                  hideHeader={true}
                  isActiveTab={chatOpen}
                  initial={(user?.email || "U")[0]?.toUpperCase() || "U"}
                />
              </Box>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">No assistant selected.</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

  


      {/* Floating Create button */}
      <Fab
        color="primary"
        aria-label="create"
        onClick={onCreate}
        sx={{ position: "fixed", bottom: 24, right: 24 }}
      >
        <AddIcon />
      </Fab>

      {/* Global Backdrop while saving (covers slow server-side training + creation) */}
      <Backdrop
        open={saving}
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal + 1, flexDirection: "column" }}
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center", maxWidth: 420 }}>
          {editingId
            ? "Updating assistant…"
            : "Creating assistant… This can take a moment while we train the initial knowledge base and apply instructions."}
        </Typography>
      </Backdrop>
    </>
  );
}
